import ClientMonitor from 'skywalking-client-js'

const enabled = process.env.VUE_APP_SKYWALKING_ENABLED === 'true'
const service = process.env.VUE_APP_SKYWALKING_SERVICE || 'cargoware-h5'
const serviceVersion = process.env.VUE_APP_VERSION || 'unknown'
const collector = getSameOriginCollector()
const reportedErrors = typeof WeakSet === 'function' ? new WeakSet() : null
let lastPerformancePage = ''

function getSameOriginCollector() {
  const configured = process.env.VUE_APP_SKYWALKING_COLLECTOR || window.location.origin
  try {
    const parsed = new URL(configured, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      console.warn('SkyWalking collector must use the Cargo H5 origin; falling back to same origin')
      return window.location.origin
    }
    return parsed.href.replace(/\/$/, '')
  } catch (error) {
    console.warn('Invalid SkyWalking collector; falling back to same origin')
    return window.location.origin
  }
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function getCrossOriginBlockRule() {
  const currentOrigin = escapeRegExp(window.location.origin)
  return new RegExp(`^(?!${currentOrigin}$).+`)
}

function redactSensitiveValues(value) {
  return String(value || '')
    .replace(/(https?:\/\/[^\s?#]+)\?[^\s#)]+/gi, '$1?[redacted]')
    .replace(/([?&](?:sessionId|token|authorization|code)=)[^&#\s]+/gi, '$1[redacted]')
    .replace(/(cookie|authorization)(\s*[:=]\s*)[^\s,;]+/gi, '$1$2[redacted]')
}

function normalizeRoutePart(value) {
  return String(value || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ':id')
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    .replace(/\/[A-Za-z0-9_-]{8,}(?=\/|$)/g, segment => /[A-Za-z]/.test(segment) && /\d/.test(segment) ? '/:id' : segment)
}

export function currentPagePath() {
  const hashPath = window.location.hash.replace(/^#/, '')
  return normalizeRoutePart(hashPath || window.location.pathname || '/') || '/'
}

function toSafeError(error) {
  const message = redactSensitiveValues(error && error.message ? error.message : error).slice(0, 1000)
  const safeError = new Error(message || 'Unknown frontend error')
  if (error && error.name) {
    safeError.name = redactSensitiveValues(error.name).slice(0, 100)
  }
  if (error && error.stack) {
    safeError.stack = redactSensitiveValues(error.stack).slice(0, 8000)
  }
  return safeError
}

export function reportFrontendError(error) {
  if (!enabled || !error) {
    return
  }
  if (reportedErrors && typeof error === 'object') {
    if (reportedErrors.has(error)) {
      return
    }
    reportedErrors.add(error)
  }
  try {
    ClientMonitor.reportFrameErrors({
      collector,
      service,
      serviceVersion,
      pagePath: currentPagePath()
    }, toSafeError(error))
  } catch (reportError) {
    console.warn('Failed to report frontend error to SkyWalking')
  }
}

export function getTraceId(response) {
  if (!response) {
    return ''
  }
  const headers = response.headers || {}
  const data = response.data || {}
  const value = headers['x-trace-id'] || headers['X-Trace-Id'] || data.messageId || ''
  const traceId = String(value).trim()
  return /^[A-Za-z0-9._-]{8,128}$/.test(traceId) ? traceId : ''
}

export function getProblemId(response) {
  if (!response || Number(response.status) < 500) {
    return ''
  }
  return getTraceId(response)
}

export function appendProblemId(message, problemId) {
  const text = message || 'Request failed'
  return problemId ? `${text}（问题编号：${problemId}）` : text
}

export function reportRouteChange(route) {
  if (!enabled) {
    return
  }
  const pagePath = normalizeRoutePart(route && route.path ? route.path : currentPagePath()) || '/'
  if (pagePath === lastPerformancePage) {
    return
  }
  lastPerformancePage = pagePath
  try {
    ClientMonitor.setPerformance({
      collector,
      service,
      serviceVersion,
      pagePath,
      useWebVitals: true
    })
  } catch (error) {
    console.warn('Failed to update SkyWalking page monitoring')
  }
}

if (enabled) {
  try {
    lastPerformancePage = currentPagePath()
    ClientMonitor.register({
      collector,
      service,
      serviceVersion,
      pagePath: currentPagePath(),
      jsErrors: false,
      apiErrors: false,
      resourceErrors: false,
      autoTracePerf: true,
      useWebVitals: true,
      enableSPA: false,
      traceSDKInternal: false,
      detailMode: false,
      noTraceOrigins: [getCrossOriginBlockRule()],
      traceTimeInterval: 30000
    })
  } catch (error) {
    console.warn('Failed to initialize SkyWalking browser monitoring')
  }
}
